import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';

const HeroPremium = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const parallax = Math.min(scrollY * 0.25, 120);
  const fade = Math.max(1 - scrollY / 600, 0);

  return (
    <section
      className="relative min-h-[calc(100vh-7.5rem)] flex items-center overflow-hidden bg-[linear-gradient(135deg,#f7f5f2_0%,#fff_58%,#efe7d5_100%)] border-b border-[#e1e7ef]"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 opacity-[0.05] bg-[radial-gradient(circle_at_1px_1px,rgb(13_27_42)_1px,transparent_0)] bg-[size:28px_28px]" />
      <div className="absolute top-0 bottom-0 right-8 md:right-16 w-px bg-gradient-to-b from-transparent via-[#C8A84B]/30 to-transparent hidden lg:block" />

      <div
        className="relative z-10 w-full max-w-[1180px] mx-auto px-6 md:px-10 lg:px-12 pt-16 pb-24"
        style={{
          transform: `translateY(${parallax}px)`,
          opacity: fade,
          transition: 'opacity 0.1s linear'
        }}
      >
        <div className="grid items-center gap-12 lg:grid-cols-[1.16fr_0.84fr]">
          <div>
            <div className="flex items-center gap-3 mb-6" data-testid="hero-eyebrow">
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#C8A84B] font-extrabold">
                THE B TREE
              </span>
            </div>

            <h1
              className="font-[Georgia,serif] font-medium text-[#0b1830] text-[48px] md:text-[62px] lg:text-[66px] leading-[1.03] tracking-[-0.035em]"
              data-testid="hero-headline"
            >
              Intelligence for
              <br />
              Better Decisions.
            </h1>

            <p
              className="mt-6 text-[19px] leading-[1.75] text-[#536177] max-w-[720px]"
              data-testid="hero-subtext"
            >
              Institutional-quality intelligence across <b className="text-[#0b1830]">Equities, Sectors, Law, Professional Finance and Civil Services</b> — transforming information into understanding, judgement and action.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <a
                href="https://www.thebtree.com/equity-intelligence"
                target="_blank"
                rel="noopener noreferrer"
                className="btn inline-flex items-center gap-3 bg-[#0b1830] text-white px-5 py-3.5 rounded-[7px] font-bold text-[14px] border border-[#0b1830] hover:bg-[#162946] transition-colors"
                data-testid="hero-explore-research-button"
              >
                Explore Intelligence
                <ArrowRight className="w-4 h-4" />
              </a>

              <a
                href="https://www.thebtree.com/subscription"
                target="_blank"
                rel="noopener noreferrer"
                className="btn inline-flex items-center gap-3 bg-white text-[#0b1830] px-5 py-3.5 rounded-[7px] font-bold text-[14px] border border-[#0b1830] hover:bg-[#f3f6fa] transition-colors"
                data-testid="hero-view-learning-button"
              >
                View Membership
              </a>
            </div>
          </div>

          <aside className="hidden lg:block border border-[#dbe2ec] bg-white/85 p-8 rounded-[14px] shadow-[0_20px_60px_rgba(15,31,55,0.08)]">
            <div className="text-[12px] font-extrabold tracking-[0.17em] uppercase text-[#C8A84B]">The B Tree Intelligence Platform</div>
            <h3 className="font-[Georgia,serif] text-[27px] leading-[1.15] text-[#0b1830] mt-3 mb-3">
              Five intelligence systems.<br />One research standard.
            </h3>
            <p className="text-[#647187] leading-[1.65]">
              Built for investors, professionals, learners and decision-makers who need structured understanding rather than more information.
            </p>

            <div className="mt-5 space-y-0 border-t border-slate-200">
              <div className="flex gap-3 py-3 border-b border-slate-200 text-[13px]">
                <b className="min-w-[88px] text-[#0b1830]">Equities</b>
                <span className="text-[#536177]">Company research, financial analysis and valuation context</span>
              </div>
              <div className="flex gap-3 py-3 border-b border-slate-200 text-[13px]">
                <b className="min-w-[88px] text-[#0b1830]">Sectors</b>
                <span className="text-[#536177]">Industry economics, disruption and future direction</span>
              </div>
              <div className="flex gap-3 py-3 border-b border-slate-200 text-[13px]">
                <b className="min-w-[88px] text-[#0b1830]">Law</b>
                <span className="text-[#536177]">Judgments, regulation and practical legal intelligence</span>
              </div>
              <div className="flex gap-3 py-3 border-b border-slate-200 text-[13px]">
                <b className="min-w-[88px] text-[#0b1830]">Professional</b>
                <span className="text-[#536177]">Finance, governance, risk and professional judgement</span>
              </div>
              <div className="flex gap-3 py-3 text-[13px]">
                <b className="min-w-[88px] text-[#0b1830]">Civil Services</b>
                <span className="text-[#536177]">Governance, policy and administrative intelligence</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default HeroPremium;
